import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "../Home/HomePage.module.css";

const API = "https://simplix.co.za/api";
//const API = "http://127.0.0.1:8000/api";
const BALANCE_CACHE_TTL = 60 * 1000;

const authenticatedFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!token) {
    window.location.href = '/#/login';
    throw new Error('No auth token');
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    },
  });
};

export default function WalletsListPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const cacheKey = (id) => `wallet_balance_${id}`;

  const getCachedBalance = (id) => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey(id)) || "null");
      if (!cached) return null;
      if (Date.now() - cached.ts > BALANCE_CACHE_TTL) return null;
      return typeof cached.balance === "number" ? cached.balance : null;
    } catch {
      return null;
    }
  };

  const setCachedBalance = (id, balance) => {
    try {
      sessionStorage.setItem(
        cacheKey(id),
        JSON.stringify({ balance, ts: Date.now() }),
      );
    } catch {}
  };

  const hasPendingAccounts = (accounts) =>
    accounts.some((a) => String(a?.status || "").toLowerCase() !== "active");

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function loadWallets() {
      setLoading(true);
      setError("");

      try {
        const email = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();

        if (!email) {
          navigate("/#/login", { replace: true });
          return;
        }

        const r = await authenticatedFetch(`${API}/accounts`);

        if (!r.ok) throw new Error("Failed to load wallets");

        const j = await r.json();

        const accounts = Array.isArray(j?.accounts) ? j.accounts : [];

        if (hasPendingAccounts(accounts)) {
          sessionStorage.setItem(
            "profile_notice",
            "Please upload your ID document for your pending account before using meters, wallets or payments.",
          );
          navigate("/profile", { replace: true });
          return;
        }

        const baseWallets = accounts
          .filter((a) => a?.new_businesscentralid)
          .map((a) => ({
            id: a.id,
            businesscentralid: a.new_businesscentralid,
            name:
              a.companyName ||
              `${a.firstName || ""} ${a.lastName || ""}`.trim() ||
              "Account",
            email: a.companyEmail || a.email || "",
            reg_or_id: a.companyRegNo || a.idNumber || "",
            status: a.status || "Pending",
            balance: getCachedBalance(a.id),
          }));

        if (cancelled) return;

        setRows(baseWallets);
        setLoading(false);

        if (baseWallets.length === 1) {
          navigate(`/accounts/${baseWallets[0].id}/wallet`, { replace: true });
          return;
        }

        const uncachedWallets = baseWallets.filter((w) => w.balance == null);

        if (!uncachedWallets.length) {
          setBalancesLoading(false);
          return;
        }

        setBalancesLoading(true);

        const balanceResults = await Promise.all(
          uncachedWallets.map(async (w) => {
            try {
              const br = await authenticatedFetch(
                `${API}/bc/customer-ledger/?account_id=${encodeURIComponent(w.id)}`,
                { signal: controller.signal },
              );

              const bj = await br.json().catch(() => ({}));
              const balance = br.ok && bj?.ok ? Number(bj.balance || 0) : null;

              if (balance != null) {
                setCachedBalance(w.id, balance);
              }

              return { id: w.id, balance };
            } catch {
              return { id: w.id, balance: null };
            }
          }),
        );

        if (cancelled) return;

        const balanceMap = new Map(
          balanceResults.map((item) => [item.id, item.balance]),
        );

        setRows((prev) =>
          prev.map((row) =>
            balanceMap.has(row.id)
              ? { ...row, balance: balanceMap.get(row.id) }
              : row,
          ),
        );
      } catch (e) {
        if (!cancelled && e.name !== "AbortError") {
          setError("Could not load wallets");
          setLoading(false);
        }
      } finally {
        if (!cancelled) setBalancesLoading(false);
      }
    }

    loadWallets();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [navigate]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;

    return rows.filter((r) =>
      [r.name, r.email, r.reg_or_id, r.status]
        .join(" ")
        .toLowerCase()
        .includes(t),
    );
  }, [rows, q]);

  const currency = (n) =>
    "R" +
    Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className={styles.page}>
      <div className={`${styles.section} ${styles.metersPageSection}`}>
        <div className={styles.metersHero}>
          <div>
            <p className={styles.sectionEyebrow}>Wallets</p>
            <h1 className={styles.h2}>My wallets</h1>
            <p className={styles.sectionIntro}>
              View accounts you can make payments and manage balances for.
            </p>
            {balancesLoading && (
              <p className={styles.micro}>Updating balances…</p>
            )}
          </div>
        </div>

        <div className={styles.meterStatsGrid}>
          <div className={styles.meterStatCard}>
            <span>Total</span>
            <strong>{rows.length}</strong>
          </div>

          <div className={styles.meterStatCard}>
            <span>Active</span>
            <strong>
              {rows.filter((r) => r.status.toLowerCase() === "active").length}
            </strong>
          </div>

          <div className={styles.meterStatCard}>
            <span>Pending</span>
            <strong>
              {rows.filter((r) => r.status.toLowerCase() !== "active").length}
            </strong>
          </div>
        </div>

        <div className={styles.metersSearchWrap}>
          <input
            className={`${styles.authInput} ${styles.metersSearchInput}`}
            placeholder="Search by name / email / ID / status"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {error && <div className={styles.noteBox}>{error}</div>}

        <div className={styles.metersTableCard}>
          <table className={`${styles.table} ${styles.metersTable}`}>
            <thead>
              <tr>
                <th>Account</th>
                <th>Email</th>
                <th>ID / Reg</th>
                <th style={{ textAlign: "right" }}>Balance</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan="6">Loading wallets…</td>
                </tr>
              )}

              {!loading &&
                filtered.map((w) => (
                  <tr key={w.id}>
                    <td>
                      <Link
                        to={`/accounts/${w.id}/wallet`}
                        className={styles.inlineBtn}
                      >
                        {w.name}
                      </Link>
                    </td>

                    <td>{w.email || "—"}</td>
                    <td>{w.reg_or_id || "—"}</td>

                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 900,
                        color:
                          w.balance == null
                            ? "#6b7280"
                            : Number(w.balance) < 0
                              ? "#139619"
                              : "#bb2323",
                      }}
                    >
                      {w.balance == null ? "Updating…" : currency(w.balance)}
                    </td>

                    <td>
                      <span className={styles.badge}>{w.status}</span>
                    </td>

                    <td>
                      <Link
                        className={styles.secondaryBtn}
                        to={`/accounts/${w.id}/wallet`}
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}

              {!loading && !filtered.length && (
                <tr>
                  <td colSpan="6">No wallets found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
